import { useState } from "react";
import axios from "axios.js";

// react-router-dom components
import { Link, useNavigate, useLocation } from "react-router-dom";

// @mui material components
import {
  Snackbar,
  Alert,
  Card,
  CircularProgress,
  Backdrop,
  Icon,
  Dialog,
  DialogActions,
  DialogContent,
  // DialogTitle,
  // DialogContentText,
  // Tooltip,
  TextField,
  Grid,
  Divider,
} from "@mui/material";
// import Grid from "@mui/material/Grid";
// import MuiLink from "@mui/material/Link";

// @mui icons
// import FacebookIcon from "@mui/icons-material/Facebook";
// import GitHubIcon from "@mui/icons-material/GitHub";
// import GoogleIcon from "@mui/icons-material/Google";

// Material Dashboard 2 React components
import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
// import MDInput from "components/MDInput";
import MDButton from "theme/components/MDButton";

// Authentication layout components
// import BasicLayout from "app/user/components/BasicLayout";
import PageLayout from "examples/LayoutContainers/PageLayout";

// Images
import bgImage from "assets/images/bg-sign-in-basic.jpeg";
// import kycImg from "assets/images/kyc/face_scan.webp";
import kycImg from "assets/images/kyc/kyc_prompt.png";
import logo from "assets/images/logos/laddar_logo_long.png";

import { useSignIn, useIsAuthenticated } from "react-auth-kit";
import { Formik } from "formik";
import * as Yup from "yup";
// import { LoadingButton } from "@mui/lab";
import usePageTitle from "app/pageTitle";
import AppConfig from "appConfig";

// inital login credentials
const initialValues = {
  email: "",
  password: "",
  remember: true,
};

// form field validation schema
const validationSchema = Yup.object().shape({
  password: Yup.string()
    .min(6, "Password must be 6 character length")
    .required("Password is required!"),
  email: Yup.string().email("Invalid Email address").required("Email is required!"),
});

function UserSignin() {
  const title = "Sign In";
  usePageTitle(title);

  const location = useLocation();
  const service = localStorage.getItem("laddar_service");
  const redirectLocation = service && location.state?.from ? location.state.from : "/laddar-os";

  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();
  if (isAuthenticated()) {
    navigate("/laddar-os");
  }

  const signIn = useSignIn();

  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("error");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [openKYCDialog, setOpenKYCDialog] = useState(false);
  const [kycLink, setKycLink] = useState(null);

  function emptyCache() {
    if ("caches" in window) {
      caches.keys().then((names) => {
        // Delete all the cache files
        names.forEach((name) => {
          caches.delete(name);
        });
      });

      // Makes sure the page reloads. Changes are only visible after you refresh.
      // window.location.reload(true);
    }
  }

  function versionRefresh() {
    emptyCache();
    // window.location.replace(redirectLocation);
    window.location = `/laddar-os?nocache=${new Date().getTime()}`;

    // const cacheWhitelist = ["alero-pwa"];
    // caches.keys().then((cacheNames) =>
    //   cacheNames.map((cacheName) => {
    //     if (cacheWhitelist.indexOf(cacheName) === -1) {
    //       return caches.delete(cacheName);
    //     }
    //     return null;
    //   })
    // );
  }

  const handleFormSubmit = async (values) => {
    setShowLoading(true);
    try {
      const json = { email_address: values.email, password: values.password };
      const response = await axios.post("portal/users/authenticate.json", json);
      if (response.data.status === "error") {
        setOpen(true);
        setShowLoading(false);
        setErrMsg(response.data.data.message);
        setSeverity("error");
      } else {
        // Handle App Update
        const currentVersion = localStorage.getItem(AppConfig.app_version_store);
        const newVersion = response.data.version;
        let shouldUpdate = false;
        if (currentVersion !== newVersion) {
          shouldUpdate = true;
          localStorage.setItem(AppConfig.app_version_store, newVersion);
        }

        // Handle KYC verification
        if (response.data.data.kyc !== null) {
          if (response.data.data.kyc.status !== true) {
            setShowLoading(false);
            setKycLink(response.data.data.kyc.vlink);
            setOpenKYCDialog(true);
            // window.open(response.data.data.kyc.vlink, "_blank");
            return;
          }
        }

        // Set token header globally
        axios.defaults.headers.common.token = response.data.data.token.token;
        localStorage.setItem(AppConfig.auth_token, response.data.data.token.token);

        const signedIn = signIn({
          token: response.data.data.token.token,
          expiresIn: response.data.data.token.duration / 60,
          // expiresIn: 120,
          tokenType: "token",
          authState: response.data.data.profile,
          // refreshToken: res.data.refreshToken, // Only if you are using refreshToken feature
          // refreshTokenExpireIn: res.data.refreshTokenExpireIn, // Only if you are using refreshToken feature
        });

        if (signedIn) {
          setOpen(true);
          setShowLoading(false);
          setErrMsg("Sign in success!");
          setSeverity("success");

          if (shouldUpdate) {
            if (
              window.confirm(
                "A new version is available and will automatically refresh to get the latest update."
              ) === true
            ) {
              versionRefresh();
            } else {
              versionRefresh();
            }
          } else {
            navigate(redirectLocation, { replace: true });
          }
        } else {
          // Throw error
          setOpen(true);
          setShowLoading(false);
          setErrMsg("Unable to sign you in.");
          setSeverity("error");
        }
      }
    } catch (e) {
      setShowLoading(false);
      setErrMsg("An error occurred. Please try again later.");
      setSeverity("error");
      setOpen(true);
    }
  };

  return (
    <PageLayout>
      <MDBox image={bgImage}>
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={showLoading}
          onClick={handleClose}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
        <Snackbar
          open={open}
          autoHideDuration={6000}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
            {errorMsg}
          </Alert>
        </Snackbar>
        <Dialog open={openKYCDialog} maxWidth="xs" fullWidth>
          <DialogContent>
            <MDBox mb={3}>
              <MDBox mt={3} align="center">
                <MDBox mt={1} mb={5}>
                  <MDTypography variant="h6" fontWeight="bold">
                    KYC Verification Required
                  </MDTypography>
                </MDBox>
                <MDBox mt={3} mb={3}>
                  <img src={kycImg} alt="" width={240} />
                </MDBox>
              </MDBox>
            </MDBox>
          </DialogContent>
          <DialogActions>
            <MDButton
              color="info"
              variant="contained"
              onClick={() => {
                window.open(kycLink, "_blank");
              }}
            >
              <Icon fontSize="lg">login_in</Icon>&nbsp; &nbsp;Verify Now
            </MDButton>
          </DialogActions>
        </Dialog>
        <MDBox
          p={3}
          sx={{
            margin: "30px auto",
            maxWidth: 800,
          }}
          mb={5}
        >
          <Grid container spacing={3}>
            <Grid
              item
              xs={0}
              md={5}
              lg={5}
              sx={{ display: { xs: "none", md: "flex", lg: "flex" } }}
            >
              <MDBox
                p={1}
                sx={{
                  verticalAlign: "center",
                }}
              >
                <MDBox
                  p={0}
                  sx={{
                    mt: {
                      xs: 0,
                      md: 5,
                      lg: 5,
                    },
                  }}
                >
                  <a href="https://laddar.africa">
                    <img src={logo} alt="" width={150} />
                  </a>
                </MDBox>
                <MDBox mt={0}>
                  <MDTypography variant="subtitle2" fontWeight="regular" fontSize={14}>
                    {AppConfig.tag_line}
                  </MDTypography>
                </MDBox>
                <MDBox p={2}>
                  <MDTypography variant="subtitle2" fontWeight="light" fontSize={15}>
                    <ul>
                      <li>Campaign Management</li>
                      <li>Team Management</li>
                      <li>Activity Tracking</li>
                      <li>Realtime Field Insights</li>
                      <li>Performance Monitor</li>
                      <li>Attendance Tracker</li>
                      <li>Dynamic Data Collection</li>
                      <li>Revenue Assurance</li>
                      <li>Micro-learning (LMS)</li>
                      <li>Commissions Management</li>
                      <li>Targets & Incentives Management</li>
                      <li>Inventory Management</li>
                      <li>Reporting & Analytics</li>
                    </ul>
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item xs={12} md={7} lg={7}>
              <MDBox mb={5}>
                <Card>
                  <MDBox pt={4} pb={3} px={3}>
                    <MDBox mb={3}>
                      <MDTypography variant="h4" fontWeight="medium" mt={1}>
                        {AppConfig.app_name}
                      </MDTypography>
                      <MDTypography display="block" variant="button" my={1}>
                        Sign in
                      </MDTypography>
                    </MDBox>
                    <MDBox mb={3}>
                      <Divider />
                    </MDBox>
                    <MDBox role="form">
                      <Formik
                        onSubmit={handleFormSubmit}
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                      >
                        {({ values, errors, touched, handleChange, handleBlur, handleSubmit }) => (
                          <form onSubmit={handleSubmit}>
                            <MDBox mb={2}>
                              {/* <MDInput type="email" label="Email" fullWidth /> */}
                              <TextField
                                fullWidth
                                size="small"
                                type="email"
                                name="email"
                                label="Email"
                                variant="outlined"
                                onBlur={handleBlur}
                                value={values.email}
                                onChange={handleChange}
                                helperText={touched.email && errors.email}
                                error={Boolean(errors.email && touched.email)}
                                sx={{ mb: 3 }}
                              />
                            </MDBox>
                            <MDBox mb={2}>
                              {/* <MDInput type="password" label="Password" fullWidth /> */}
                              <TextField
                                fullWidth
                                size="small"
                                name="password"
                                type="password"
                                label="Password"
                                variant="outlined"
                                onBlur={handleBlur}
                                value={values.password}
                                onChange={handleChange}
                                helperText={touched.password && errors.password}
                                error={Boolean(errors.password && touched.password)}
                                sx={{ mb: 1.5 }}
                              />
                            </MDBox>
                            <MDBox mt={4} mb={1}>
                              <MDButton
                                variant="gradient"
                                color={AppConfig.sidenav_active_menu_color}
                                type="submit"
                                fullWidth
                              >
                                sign in
                              </MDButton>
                              {/* <LoadingButton
                      fullWidth
                      type="submit"
                      color="primary"
                      loading={loading}
                      variant="contained"
                      sx={{ my: 2 }}
                    >
                      <div sx={{ color: "white" }}>Login</div>
                    </LoadingButton> */}
                            </MDBox>
                          </form>
                        )}
                      </Formik>
                      <MDBox mt={3} mb={1} textAlign="center">
                        <MDTypography variant="button" color="text">
                          Don&apos;t have an account?{" "}
                          <MDTypography
                            component={Link}
                            to="/sign-up"
                            variant="button"
                            color={AppConfig.sidenav_active_menu_color}
                            fontWeight="medium"
                            textGradient
                          >
                            Sign up
                          </MDTypography>
                        </MDTypography>
                      </MDBox>
                      <MDBox mt={3} mb={1} textAlign="center">
                        <MDTypography variant="button" color="text">
                          <MDTypography
                            component={Link}
                            to="/forgot-password"
                            variant="button"
                            color={AppConfig.sidenav_active_menu_color}
                            fontWeight="medium"
                            textGradient
                          >
                            Forgot password?
                          </MDTypography>
                        </MDTypography>
                      </MDBox>
                    </MDBox>
                  </MDBox>
                </Card>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </PageLayout>
  );
}

export default UserSignin;
